import { useState, useEffect, useCallback } from "react";
import {
  fetchGoogleReviewsStatusFromAdmin,
  refreshGoogleReviewsFromAdmin,
} from "../../utils/contentApi";

function formatDate(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function GoogleReviewsAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  };

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchGoogleReviewsStatusFromAdmin();
      setData(result);
    } catch {
      showMessage("Erreur lors du chargement du statut des avis.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setMessage({ text: "", type: "" });
      const result = await refreshGoogleReviewsFromAdmin();
      setData(result);
      if (result?.reviews?.length > 0) {
        showMessage(
          `Avis mis à jour avec succès — ${result.reviews.length} avis chargé(s).`,
          "success",
        );
      } else {
        showMessage(
          "Rafraîchissement effectué mais aucun avis retourné. Vérifiez la clé API et l'ID du lieu.",
          "warning",
        );
      }
    } catch (err) {
      showMessage(
        err?.message || "Erreur lors du rafraîchissement des avis.",
        "error",
      );
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="admin-section">
      <h1 className="admin-section-title">Avis Google</h1>
      <p className="admin-section-description">
        Les avis Google sont mis en cache et rafraîchis automatiquement toutes
        les 24&nbsp;heures. Utilisez le bouton ci-dessous pour forcer une mise à
        jour immédiate.
      </p>
      <p className="admin-section-description">
        Google Places ne renvoie que 5 avis maximum par lieu. Le nombre total
        d'avis affiché par Google peut donc être supérieur au nombre d'avis que
        l'API autorise à récupérer.
      </p>

      {message.text && (
        <div
          className={`admin-message ${message.type === "error" ? "admin-message--error" : message.type === "warning" ? "admin-message--warning" : "admin-message--success"}`}
        >
          {message.text}
        </div>
      )}

      {!loading && data?.warning && (
        <div className="admin-message admin-message--warning">
          {data.warning}
        </div>
      )}

      <div className="admin-card" style={{ marginBottom: "1.5rem" }}>
        <h2 className="admin-card-title">Statut du cache</h2>
        {loading ? (
          <p>Chargement…</p>
        ) : (
          <table className="admin-info-table">
            <tbody>
              <tr>
                <th>Dernière mise à jour</th>
                <td>{formatDate(data?.updatedAt)}</td>
              </tr>
              <tr>
                <th>Source</th>
                <td>{data?.source || "—"}</td>
              </tr>
              <tr>
                <th>Note moyenne</th>
                <td>
                  {data?.rating != null
                    ? `${data.rating} / 5 (${data.userRatingCount ?? 0} avis)`
                    : "—"}
                </td>
              </tr>
              <tr>
                <th>Avis en cache</th>
                <td>{data?.reviews?.length ?? 0}</td>
              </tr>
              <tr>
                <th>Limite Google API</th>
                <td>{data?.maxReviewsReturnedByGoogle ?? 5} avis maximum</td>
              </tr>
              <tr>
                <th>Cache périmé</th>
                <td>{data?.isStale ? "Oui" : "Non"}</td>
              </tr>
              <tr>
                <th>Tri par plus récents</th>
                <td>{data?.newestSortAvailable ? "Actif" : "Indisponible"}</td>
              </tr>
              <tr>
                <th>Lieu (placeId)</th>
                <td style={{ wordBreak: "break-all" }}>
                  {data?.placeId || "— (non configuré)"}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      <button
        className="btn-primary"
        onClick={handleRefresh}
        disabled={refreshing || loading}
        style={{ minWidth: "220px" }}
      >
        {refreshing ? "Rafraîchissement…" : "Forcer la mise à jour des avis"}
      </button>

      {data?.reviews?.length > 0 && (
        <div className="admin-card" style={{ marginTop: "2rem" }}>
          <h2 className="admin-card-title">Avis actuellement en cache</h2>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {data.reviews.map((review) => (
              <li
                key={review.id}
                style={{
                  borderBottom: "1px solid #eee",
                  padding: "0.75rem 0",
                }}
              >
                <strong>{review.authorName}</strong>
                {review.rating != null && (
                  <span style={{ marginLeft: "0.5rem", color: "#f5a623" }}>
                    {"★".repeat(Math.round(review.rating))}
                  </span>
                )}
                <span
                  style={{ marginLeft: "0.75rem", color: "#888", fontSize: "0.85em" }}
                >
                  {review.relativeDate || formatDate(review.publishedAt)}
                </span>
                <p style={{ margin: "0.25rem 0 0", color: "#444" }}>
                  {review.text}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default GoogleReviewsAdmin;
